'use client';

import React, { useState, useCallback } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { generateBookingRef } from '@/lib/pricing/pricingEngine';
import { SANDBOX_WARNING, PAYMENT_PROVIDER_INFO } from '@/lib/payments/nepal-payments';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';

// Nepal train routes (Janakpur Railway + planned routes)
const TRAIN_STATIONS = [
  'Janakpur', 'Jayanagar', 'Bijalpura', 'Khajuri', 'Parbaha',
  'Kathmandu (Planned)', 'Pokhara (Planned)', 'Raxaul (India)',
];

interface TrainTrip {
  id: string;
  operator: string;
  trainName: string;
  trainNumber: string;
  from: string;
  to: string;
  departureISO: string;
  arrivalISO: string;
  durationMinutes: number;
  classes: TrainClass[];
  isDemo: boolean;
  demoLabel: string;
  routeNote?: string;
}

interface TrainClass {
  name: string;
  code: string;
  availableSeats: number;
  baseFare: number;
  tax: number;
  serviceFee: number;
  totalFare: number;
}

function generateDemoTrainTrips(from: string, to: string, dateStr: string, passengers: number, travelClass: string): TrainTrip[] {
  if (!from || !to || from === to) return [];
  const base = new Date(`${dateStr}T07:00:00+05:45`);
  const isPlanned = from.includes('Planned') || to.includes('Planned');

  const trains = [
    { name: 'Janakpur Express', number: 'JE-101', duration: 90, fareMultiplier: 1.0 },
    { name: 'Mithila Passenger', number: 'MP-201', duration: 110, fareMultiplier: 0.8 },
  ];

  if (isPlanned) {
    return [{
      id: `train-planned-${dateStr}`,
      operator: 'Nepal Railways (Planned)',
      trainName: 'Proposed Route',
      trainNumber: 'TBD',
      from,
      to,
      departureISO: base.toISOString(),
      arrivalISO: new Date(base.getTime() + 180 * 60000).toISOString(),
      durationMinutes: 180,
      classes: [{ name: 'General', code: 'GEN', availableSeats: 0, baseFare: 0, tax: 0, serviceFee: 0, totalFare: 0 }],
      isDemo: true,
      demoLabel: '⚠️ Planned Route — Not yet operational',
      routeNote: 'This railway line is under planning/construction. No service available yet.',
    }];
  }

  return trains.map((t, i) => {
    const dep = new Date(base.getTime() + i * 120 * 60000);
    const arr = new Date(dep.getTime() + t.duration * 60000);
    const baseFarePerPax = Math.round(150 * t.fareMultiplier);
    const classes: TrainClass[] = [
      { name: 'General', code: 'GEN', availableSeats: 80, baseFare: baseFarePerPax * passengers, tax: Math.round(baseFarePerPax * passengers * 0.13), serviceFee: Math.round(baseFarePerPax * passengers * 0.02), totalFare: 0 },
      { name: 'First Class', code: '1A', availableSeats: 20, baseFare: baseFarePerPax * 2 * passengers, tax: Math.round(baseFarePerPax * 2 * passengers * 0.13), serviceFee: Math.round(baseFarePerPax * 2 * passengers * 0.02), totalFare: 0 },
    ].map(c => ({ ...c, totalFare: c.baseFare + c.tax + c.serviceFee }));

    return {
      id: `train-${i + 1}-${dateStr}`,
      operator: 'Janakpur Railway',
      trainName: t.name,
      trainNumber: t.number,
      from,
      to,
      departureISO: dep.toISOString(),
      arrivalISO: arr.toISOString(),
      durationMinutes: t.duration,
      classes,
      isDemo: true,
      demoLabel: '⚠️ Demo — Estimated schedule. Verify with Janakpur Railway for confirmed tickets.',
    };
  });
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-NP', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kathmandu' });
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-NP', { day: 'numeric', month: 'short', timeZone: 'Asia/Kathmandu' });
}

function formatDuration(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function isNextDay(depISO: string, arrISO: string): boolean {
  const dep = new Date(depISO).toLocaleDateString('en-CA', { timeZone: 'Asia/Kathmandu' });
  const arr = new Date(arrISO).toLocaleDateString('en-CA', { timeZone: 'Asia/Kathmandu' });
  return arr > dep;
}

const PAYMENT_OPTIONS = ['esewa', 'khalti', 'fonepay', 'pay_later'] as const;

export default function TrainTicketsPage() {
  const { user, profile } = useAuth();
  const [from, setFrom] = useState('Janakpur');
  const [to, setTo] = useState('Jayanagar');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [passengers, setPassengers] = useState(1);
  const [travelClass, setTravelClass] = useState('all');
  const [searched, setSearched] = useState(false);
  const [results, setResults] = useState<TrainTrip[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState<TrainTrip | null>(null);
  const [selectedClass, setSelectedClass] = useState<TrainClass | null>(null);
  const [showBooking, setShowBooking] = useState(false);

  const [fullName, setFullName] = useState(profile?.full_name || '');
  const [email, setEmail] = useState(profile?.email || '');
  const [phone, setPhone] = useState(profile?.phone || '');
  const [paymentMethod, setPaymentMethod] = useState<'esewa' | 'khalti' | 'fonepay' | 'pay_later'>('esewa');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [bookingRef, setBookingRef] = useState('');
  const [bookingError, setBookingError] = useState('');

  const today = new Date().toISOString().split('T')[0];

  const handleSearch = useCallback(() => {
    if (!from || !to || from === to) return;
    setLoading(true);
    setSearched(true);
    setSelectedTrip(null);
    setShowBooking(false);
    setTimeout(() => {
      setResults(generateDemoTrainTrips(from, to, date, passengers, travelClass));
      setLoading(false);
    }, 600);
  }, [from, to, date, passengers, travelClass]);

  const handleSelectClass = (trip: TrainTrip, cls: TrainClass) => {
    if (!user) { window.location.href = `/login?redirect=/train-tickets`; return; }
    if (cls.availableSeats === 0) return;
    setSelectedTrip(trip);
    setSelectedClass(cls);
    setFullName(profile?.full_name || '');
    setEmail(profile?.email || '');
    setPhone(profile?.phone || '');
    setShowBooking(true);
    setSubmitted(false);
    setBookingError('');
  };

  const handleBookSubmit = async () => {
    if (!selectedTrip || !selectedClass) return;
    setBookingError('');
    if (!fullName.trim()) { setBookingError('Full name is required.'); return; }
    if (!email.trim()) { setBookingError('Email is required.'); return; }
    setSubmitting(true);
    try {
      const ref = generateBookingRef('train');
      const supabase = createClient();
      await supabase.from('bookings').insert({
        tourist_id: user?.id || null,
        service_type: 'train',
        service_id: selectedTrip.id,
        booking_reference: ref,
        booking_name: `Train: ${selectedTrip.from} → ${selectedTrip.to}`,
        full_name: fullName,
        email,
        phone,
        start_date: date,
        travelers: passengers,
        origin: selectedTrip.from,
        destination: selectedTrip.to,
        base_amount: selectedClass.baseFare,
        tax: selectedClass.tax,
        service_fee: selectedClass.serviceFee,
        total_price: selectedClass.totalFare,
        currency: 'NPR',
        booking_status: 'pending',
        payment_status: 'unpaid',
        booking_type: 'train',
        is_demo: true,
        price_snapshot: JSON.stringify({ operator: selectedTrip.operator, trainName: selectedTrip.trainName, class: selectedClass.name, departure: selectedTrip.departureISO, arrival: selectedTrip.arrivalISO }),
      });
      setBookingRef(ref);
      setSubmitted(true);
    } catch {
      setBookingError('Booking failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-navy">
      <Header />

      <div className="pt-24 pb-8 px-4" style={{ background: 'linear-gradient(180deg, #0E3570 0%, #0B2D5B 100%)' }}>
        <div className="max-w-5xl mx-auto">
          <div className="section-eyebrow mb-2">Nepal Rail Network</div>
          <h1 className="heading-display text-3xl md:text-4xl text-snow mb-2">
            Train <span className="italic text-gold">Tickets</span>
          </h1>
          <p className="text-snow/50 text-sm">Janakpur Railway and planned national rail routes across Nepal.</p>
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs" style={{ background: 'rgba(245,192,48,0.1)', border: '1px solid rgba(245,192,48,0.2)', color: 'rgba(245,192,48,0.8)' }}>
            ⚠️ Demo Mode — Only Janakpur Railway is operational. Other routes are planned.
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Search form */}
        <div className="yn-card rounded-2xl p-6 mb-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="text-snow/50 text-xs font-semibold uppercase tracking-wider mb-1.5 block">From</label>
              <select value={from} onChange={e => setFrom(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-snow focus:outline-none focus:border-gold/40">
                {TRAIN_STATIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-snow/50 text-xs font-semibold uppercase tracking-wider mb-1.5 block">To</label>
              <select value={to} onChange={e => setTo(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-snow focus:outline-none focus:border-gold/40">
                {TRAIN_STATIONS.filter(s => s !== from).map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-snow/50 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Date</label>
              <input type="date" value={date} min={today} onChange={e => setDate(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-snow focus:outline-none focus:border-gold/40" />
            </div>
            <div>
              <label className="text-snow/50 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Passengers</label>
              <select value={passengers} onChange={e => setPassengers(Number(e.target.value))}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-3 text-snow focus:outline-none focus:border-gold/40">
                {[1,2,3,4,5,6].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <button onClick={handleSearch} disabled={from === to}
                className="w-full py-3 bg-gold text-navy font-bold rounded-xl hover:bg-gold/90 transition-colors disabled:opacity-40">
                🔍 Search
              </button>
            </div>
          </div>
        </div>

        {/* Results */}
        {loading && (
          <div className="space-y-3">
            {[1,2].map(i => <div key={i} className="h-40 bg-white/5 rounded-2xl animate-pulse" />)}
          </div>
        )}

        {!loading && searched && results.length === 0 && (
          <div className="text-center py-16">
            <div className="text-4xl mb-3">🚂</div>
            <h3 className="text-snow font-semibold mb-2">No trains found</h3>
            <p className="text-snow/40 text-sm">Try Janakpur → Jayanagar for available routes</p>
          </div>
        )}

        {!loading && results.length > 0 && !showBooking && (
          <div className="space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-snow font-bold">{results.length} train{results.length > 1 ? 's' : ''} found · {from} → {to}</h2>
              <span className="text-snow/40 text-xs">{date} · {passengers} passenger{passengers > 1 ? 's' : ''}</span>
            </div>
            {results.map(trip => (
              <div key={trip.id} className="yn-card rounded-2xl overflow-hidden">
                <div className="px-4 py-1.5 text-[10px]" style={{ background: 'rgba(245,192,48,0.08)', borderBottom: '1px solid rgba(245,192,48,0.15)', color: 'rgba(245,192,48,0.7)' }}>
                  {trip.demoLabel}
                </div>
                <div className="p-5">
                  {trip.routeNote && (
                    <div className="bg-amber-400/10 border border-amber-400/20 rounded-xl p-3 text-xs text-amber-400/80 mb-4">
                      {trip.routeNote}
                    </div>
                  )}
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-2xl">🚂</span>
                    <div>
                      <div className="text-snow font-bold text-sm">{trip.trainName}</div>
                      <div className="text-snow/40 text-xs">{trip.operator} · {trip.trainNumber}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mb-4">
                    <div>
                      <div className="text-snow font-bold text-xl tabular-nums">{formatTime(trip.departureISO)}</div>
                      <div className="text-snow/50 text-xs">{trip.from}</div>
                      <div className="text-snow/30 text-[10px]">{formatDate(trip.departureISO)}</div>
                    </div>
                    <div className="flex flex-col items-center flex-1">
                      <div className="text-snow/40 text-xs mb-1">{formatDuration(trip.durationMinutes)}</div>
                      <div className="w-full h-px bg-white/20" />
                      <div className="text-snow/30 text-[10px] mt-1">Direct</div>
                    </div>
                    <div className="text-right">
                      <div className="text-snow font-bold text-xl tabular-nums">
                        {formatTime(trip.arrivalISO)}
                        {isNextDay(trip.departureISO, trip.arrivalISO) && (
                          <span className="text-xs text-amber-400 ml-1">+1 day</span>
                        )}
                      </div>
                      <div className="text-snow/50 text-xs">{trip.to}</div>
                      <div className="text-snow/30 text-[10px]">{formatDate(trip.arrivalISO)}</div>
                    </div>
                  </div>

                  {/* Class selection */}
                  {!trip.routeNote && (
                    <div className="space-y-2">
                      <div className="text-snow/40 text-xs font-semibold uppercase tracking-wider mb-2">Select Class</div>
                      {trip.classes.map(cls => (
                        <div key={cls.code} className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10">
                          <div>
                            <div className="text-snow text-sm font-semibold">{cls.name} ({cls.code})</div>
                            <div className="text-snow/40 text-xs">{cls.availableSeats} seats available</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-right">
                              <div className="text-gold font-bold text-sm">NPR {cls.totalFare.toLocaleString()}</div>
                              <div className="text-snow/30 text-[10px]">incl. tax</div>
                            </div>
                            <button
                              onClick={() => handleSelectClass(trip, cls)}
                              disabled={cls.availableSeats === 0}
                              className="px-3 py-2 bg-gold text-navy font-bold rounded-xl text-xs hover:bg-gold/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                              {cls.availableSeats === 0 ? 'Full' : 'Quick'}
                            </button>
                            <Link href={`/train-tickets/${trip.id}/book`}
                              className="px-3 py-2 rounded-xl text-xs font-semibold"
                              style={{ background: 'rgba(245,192,48,0.12)', color: 'var(--gold)', border: '1px solid rgba(245,192,48,0.3)' }}>
                              Form →
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Booking panel */}
        {showBooking && selectedTrip && selectedClass && !submitted && (
          <div className="max-w-2xl mx-auto">
            <button onClick={() => setShowBooking(false)} className="text-snow/40 text-sm hover:text-snow/70 mb-4 inline-flex items-center gap-1">← Back to results</button>
            <div className="yn-card rounded-2xl p-6 space-y-5">
              <h2 className="text-snow font-bold text-lg">Complete Booking</h2>

              <div className="bg-white/5 rounded-xl p-4 text-sm space-y-2">
                <div className="flex justify-between"><span className="text-snow/50">Train</span><span className="text-snow">{selectedTrip.trainName}</span></div>
                <div className="flex justify-between"><span className="text-snow/50">Route</span><span className="text-snow">{selectedTrip.from} → {selectedTrip.to}</span></div>
                <div className="flex justify-between"><span className="text-snow/50">Departure</span><span className="text-snow">{formatTime(selectedTrip.departureISO)} · {formatDate(selectedTrip.departureISO)}</span></div>
                <div className="flex justify-between"><span className="text-snow/50">Arrival</span>
                  <span className="text-snow">{formatTime(selectedTrip.arrivalISO)} · {formatDate(selectedTrip.arrivalISO)}
                    {isNextDay(selectedTrip.departureISO, selectedTrip.arrivalISO) && <span className="text-amber-400 text-xs ml-1">+1 day</span>}
                  </span>
                </div>
                <div className="flex justify-between"><span className="text-snow/50">Class</span><span className="text-snow">{selectedClass.name}</span></div>
                <div className="flex justify-between"><span className="text-snow/50">Passengers</span><span className="text-snow">{passengers}</span></div>
                <div className="border-t border-white/10 pt-2 space-y-1">
                  <div className="flex justify-between text-snow/50 text-xs"><span>Base fare</span><span>NPR {selectedClass.baseFare.toLocaleString()}</span></div>
                  <div className="flex justify-between text-snow/50 text-xs"><span>Tax (13%)</span><span>NPR {selectedClass.tax.toLocaleString()}</span></div>
                  <div className="flex justify-between text-snow/50 text-xs"><span>Service fee (2%)</span><span>NPR {selectedClass.serviceFee.toLocaleString()}</span></div>
                  <div className="flex justify-between font-bold pt-1 border-t border-white/10">
                    <span className="text-snow">Total</span><span className="text-gold">NPR {selectedClass.totalFare.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="bg-amber-400/10 border border-amber-400/20 rounded-xl p-3 text-xs text-amber-400/80">
                ⚠️ Demo Mode: No real ticket will be issued. Use &quot;Request Booking Assistance&quot; for actual bookings.
              </div>

              <div className="space-y-3">
                <div>
                  <label className="text-snow/50 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Full Name *</label>
                  <input value={fullName} onChange={e => setFullName(e.target.value)} placeholder="Your full name"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-snow placeholder-snow/30 focus:outline-none focus:border-gold/40" />
                </div>
                <div>
                  <label className="text-snow/50 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Email *</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-snow placeholder-snow/30 focus:outline-none focus:border-gold/40" />
                </div>
                <div>
                  <label className="text-snow/50 text-xs font-semibold uppercase tracking-wider mb-1.5 block">Phone</label>
                  <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+977 98XXXXXXXX"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-snow placeholder-snow/30 focus:outline-none focus:border-gold/40" />
                </div>
              </div>

              <div>
                <label className="text-snow/50 text-xs font-semibold uppercase tracking-wider mb-2 block">Payment Method</label>
                <div className="grid grid-cols-2 gap-2">
                  {PAYMENT_OPTIONS.map(pm => {
                    const info = PAYMENT_PROVIDER_INFO[pm];
                    return (
                      <button key={pm} type="button" onClick={() => setPaymentMethod(pm)}
                        className={`p-3 rounded-xl border text-left transition-all ${paymentMethod === pm ? 'border-gold/50 bg-gold/5' : `border-white/10 ${info.color} hover:border-white/20`}`}>
                        <div className="flex items-center gap-2">
                          <span>{info.icon}</span>
                          <span className="text-snow text-xs font-semibold">{info.label}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <p className="text-amber-400/60 text-[10px] mt-2">⚠️ {SANDBOX_WARNING.en}</p>
              </div>

              {bookingError && <p className="text-red-400 text-sm bg-red-400/10 rounded-xl px-4 py-3">{bookingError}</p>}
              <div className="flex gap-3">
                <button onClick={() => setShowBooking(false)} className="flex-1 py-3 border border-white/20 text-snow/70 rounded-xl hover:border-white/40 transition-colors">Cancel</button>
                <button onClick={handleBookSubmit} disabled={submitting}
                  className="flex-1 py-3 bg-gold text-navy font-bold rounded-xl hover:bg-gold/90 transition-colors disabled:opacity-50">
                  {submitting ? 'Processing...' : 'Request Booking Assistance'}
                </button>
              </div>
            </div>
          </div>
        )}

        {submitted && selectedTrip && selectedClass && (
          <div className="max-w-2xl mx-auto text-center py-10">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-snow text-2xl font-bold mb-2">Booking Request Received!</h2>
            <p className="text-snow/60 text-sm mb-1">{selectedTrip.from} → {selectedTrip.to} · {selectedClass.name}</p>
            <div className="yn-card rounded-2xl p-4 my-6 text-left max-w-sm mx-auto">
              <div className="text-xs text-snow/40 mb-1">Reference</div>
              <div className="text-gold font-bold font-mono text-sm">{bookingRef}</div>
              <div className="text-xs text-snow/40 mt-3 mb-1">Total</div>
              <div className="text-gold font-bold">NPR {selectedClass.totalFare.toLocaleString()}</div>
              <div className="text-xs text-amber-400/70 mt-3">⚠️ Demo mode — no real ticket issued. Our team will contact you to confirm.</div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link href="/booking/my-bookings" className="btn-gold px-6 py-3 rounded-xl text-sm font-semibold">My Bookings</Link>
              <button onClick={() => { setSubmitted(false); setShowBooking(false); setSelectedTrip(null); }} className="btn-outline-gold px-6 py-3 rounded-xl text-sm font-semibold">Search Again</button>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
}
